import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import SeoHead from '../components/SeoHead'
import { getCmsPage } from '../lib/cms'
import { getOptimizedImageUrl } from '../lib/utils'

export default function ServicesPage() {
  const [pageData, setPageData] = useState(null)

  useEffect(() => {
    let isMounted = true
    getCmsPage('ydelser').then((page) => {
      if (isMounted) setPageData(page)
    })
    return () => {
      isMounted = false
    }
  }, [])

  const clioImg = 'https://assets.auxodev.dk/assets/d6e713fb-6411-4075-ab6a-5936b7a50d5a/2dee4a-faa19ed76fd948908b3c8c846769fc3e-mv2-d-1800-1800-s-2.jpg'
  const hypexImg = 'https://assets.auxodev.dk/assets/d6e713fb-6411-4075-ab6a-5936b7a50d5a/2dee4a-b1d6f3a7c07e4c09a8e1566b4ae5990e-mv2.png'
  const speakerImg = 'https://assets.auxodev.dk/assets/d6e713fb-6411-4075-ab6a-5936b7a50d5a/2dee4a-10d0de04f8d84061b64ec76601f0ef3c-mv2.jpg'

  return (
    <div className="bg-[#121214] text-[#F5F3EF]">
      <SeoHead
        title={pageData?.seo_title || "Ydelser | Højtalerudvikling, CLIO Måling & Hypex Forstærkere - JARCon"}
        description={
          pageData?.meta_description ||
          "Professionel højtalerudvikling på bestilling, præcisionsudmåling med CLIO pocket samt rådgivning og forhandling af Hypex Class D forstærkere og DSP hos JARCon ApS."
        }
        ogImage={clioImg}
        canonicalUrl="https://www.jarcon.dk/ydelser"
      />

      {/* Hero */}
      <section className="relative border-b border-white/10 bg-[#0E0E10] py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block text-xs uppercase tracking-widest text-[#C87D46] font-medium mb-4">
            Kompetencer & Rådgivning
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-[#F5F3EF]">
            Ydelser
          </h1>
          <p className="mt-4 font-serif italic text-lg sm:text-2xl text-[#E5E2DB] max-w-2xl mx-auto">
            “Højtalerudvikling på bestilling, præcisionsudmåling og forhandling af high-end elektronik”
          </p>
        </div>
      </section>

      {/* Services List */}
      <section className="py-16 sm:py-24 border-b border-white/10 space-y-16 sm:space-y-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 space-y-16 sm:space-y-24">
          {/* Service 1: Højtaler udvikling */}
          <div id="hoejtaler-udvikling" className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center scroll-mt-28">
            <div className="lg:col-span-6 space-y-5">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-[#C87D46] font-medium">
                <i className="fa-solid fa-screwdriver-wrench" aria-hidden="true" />
                <span>Specialbyg & Rådgivning</span>
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl text-[#F5F3EF]">
                Højtaler udvikling
              </h2>
              <div className="space-y-4 text-sm sm:text-base text-[#A8A49C] leading-relaxed">
                <p>
                  Da jeg gerne vil dele ud af min erfaring, tilbyder jeg at bygge højtalere på bestilling, men også hvis du har brug for inspiration til dit eget projekt.
                </p>
                <p className="text-[#E5E2DB] font-medium">
                  Ring eller skriv for at få en dialog om ønsker og muligheder for at skabe din helt egen højtaler
                </p>
                <p>
                  Uanset om du søger en klassisk tovejs monitor i ædelt træ, et kompromisløst trevejs gulvhøjtalersystem eller optimering af et eksisterende delefilter, kan vi tage en grundig snak om komponentvalg, kabinetvolumen og delefrekvenser.
                </p>
              </div>
              <div className="pt-2">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 rounded-sm bg-[#C87D46] px-6 py-3 text-xs font-medium text-white hover:bg-[#D98E58] transition-colors"
                >
                  <i className="fa-solid fa-comments text-xs" aria-hidden="true" />
                  <span>Start en dialog om din højtaler</span>
                </Link>
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="overflow-hidden rounded-sm border border-white/10 bg-[#18181C] shadow-2xl">
                <img
                  src={getOptimizedImageUrl(speakerImg, { width: 900, quality: 85 })}
                  alt="Højtalerudvikling og anlæg"
                  className="w-full aspect-[4/3] object-cover"
                />
                <div className="p-4 bg-[#141418] text-xs text-[#E5E2DB]">
                  Mit anlæg som det ser ud PT – højtalerudvikling med fokus på nerve og nærvær
                </div>
              </div>
            </div>
          </div>

          {/* Service 2: Højtaler udmåling */}
          <div id="hoejtaler-udmaaling" className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center scroll-mt-28">
            <div className="lg:col-span-6 order-2 lg:order-1">
              <div className="overflow-hidden rounded-sm border border-white/10 bg-[#18181C] p-6 shadow-2xl flex items-center justify-center">
                <img
                  src={getOptimizedImageUrl(clioImg, { width: 600, quality: 85 })}
                  alt="CLIO Pocket målesystem"
                  className="w-full max-w-md object-contain"
                />
              </div>
            </div>

            <div className="lg:col-span-6 space-y-5 order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-[#C87D46] font-medium">
                <i className="fa-solid fa-chart-line" aria-hidden="true" />
                <span>Akustisk Præcision</span>
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl text-[#F5F3EF]">
                Højtaler udmåling
              </h2>
              <div className="space-y-4 text-sm sm:text-base text-[#A8A49C] leading-relaxed">
                <p className="text-[#F5F3EF]">
                  Jeg tilbyder udmåling af dine højtalere med CLIO pocket målesystem:
                </p>
                <div className="rounded-sm border border-white/10 bg-[#18181C] p-5 space-y-3">
                  <div className="flex items-center gap-3 text-sm text-[#E5E2DB]">
                    <span className="h-2 w-2 rounded-full bg-[#C87D46]" />
                    <span><strong>Frekvensgang:</strong> Præcis akustisk respons on-axis og off-axis.</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-[#E5E2DB]">
                    <span className="h-2 w-2 rounded-full bg-[#C87D46]" />
                    <span><strong>Vandfaldskurve (CSD):</strong> Analyse af resonanser, dæmpning og efterklangstid.</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-[#E5E2DB]">
                    <span className="h-2 w-2 rounded-full bg-[#C87D46]" />
                    <span><strong>TS parametre m.m.:</strong> Thiele/Small parametre for rå enheder og kabinetberegning.</span>
                  </div>
                </div>
                <p>
                  Målingerne giver et objektivt grundlag for finjustering af delefiltre og kabinetdæmpning, så du opnår maksimal opløsning og neutral klangbalance.
                </p>
              </div>
              <div className="pt-2">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 rounded-sm bg-[#C87D46] px-6 py-3 text-xs font-medium text-white hover:bg-[#D98E58] transition-colors"
                >
                  <i className="fa-solid fa-calendar-check text-xs" aria-hidden="true" />
                  <span>Book udmåling af dine højtalere</span>
                </Link>
              </div>
            </div>
          </div>

          {/* Service 3: Forhandling af Hypex Elektronik */}
          <div id="hypex-elektronik" className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center scroll-mt-28">
            <div className="lg:col-span-6 space-y-5">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-[#C87D46] font-medium">
                <i className="fa-solid fa-bolt" aria-hidden="true" />
                <span>Autoriseret Forhandler</span>
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl text-[#F5F3EF]">
                Forhandling af Hypex Elektronik
              </h2>
              <div className="space-y-4 text-sm sm:text-base text-[#A8A49C] leading-relaxed">
                <p className="text-[#F5F3EF] text-lg font-serif">
                  Forhandling af Hypex Class D forstærkere til fx aktive højtalere, samt DSP og forsyninger, alle i meget høj kvalitet
                </p>
                <p>
                  Hypex Electronics er verdensførende inden for high-end Class D forstærkerteknologi (Ncore, FusionAmp, m.fl.), der leverer ekstrem kontrol, lav forvrængning og overlegen dynamik med høj energieffektivitet.
                </p>
                <p>
                  Hvad enten du bygger aktive studiemonitorer, kraftfulde subwoofere eller passive/aktive hybridanlæg, rådgiver vi om valg af rette Hypex moduler og DSP opsætning.
                </p>
              </div>
              <div className="pt-2">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 rounded-sm bg-[#C87D46] px-6 py-3 text-xs font-medium text-white hover:bg-[#D98E58] transition-colors"
                >
                  <i className="fa-solid fa-envelope text-xs" aria-hidden="true" />
                  <span>Forespørg på Hypex elektronik</span>
                </Link>
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="overflow-hidden rounded-sm border border-white/10 bg-[#18181C] p-8 shadow-2xl flex items-center justify-center">
                <img
                  src={getOptimizedImageUrl(hypexImg, { width: 500, quality: 90 })}
                  alt="Hypex Electronics logo"
                  className="w-full max-w-xs object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 sm:py-24 bg-[#0E0E10] text-center">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-3xl sm:text-4xl text-[#F5F3EF]">
            Har du et projekt, vi skal kigge på sammen?
          </h2>
          <p className="mt-4 text-sm text-[#A8A49C]">
            Kom forbi til et lyt og en god snak om dine ambitioner.
          </p>
          <div className="mt-8 flex justify-center">
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 rounded-sm bg-[#C87D46] px-8 py-3.5 text-sm font-medium text-white hover:bg-[#D98E58] transition-colors"
            >
              <i className="fa-solid fa-mug-hot text-xs" aria-hidden="true" />
              <span>Kontakt for aftale</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
